//
import DropdownInput from "../widgets/DropdownInput";
import StringUtils    from "./StringUtils";

//

export class TimezoneUtils
{

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Builds the list of dropdown choices for the given IANA timezone names, formatted
     * according to the requested value/label types and sorted by UTC offset ( furthest
     * ahead of UTC first ).
     *
     * @param zones - IANA timezone names ( e.g. "America/New_York" ) to build choices for.
     * @param valueType - Determines what each choice's `value` is set to.
     * @param labelType - Determines what each choice's visible `label` is set to.
     * @param dense - When `labelType` is `CITY`, shows just the city portion ( "New York" )
     *                instead of the full "Region/City" name.
     * @param at - The moment to resolve offsets/abbreviations for ( defaults to now ). A zone's
     *             offset and abbreviation ( EST vs EDT ) depend on whether that moment is in DST,
     *             so callers scheduling a future send should pass the send date.
     * @returns Dropdown choices sorted by UTC offset, descending.
     */
    public static choices( zones : Array<string>, valueType : TimezoneUtils.ValueType, labelType : TimezoneUtils.LabelType, dense : boolean = false, at : Date = new Date() ) : Array<DropdownInput.Choice>
    {
        // a half-typed date arrives as an Invalid Date, which Intl.DateTimeFormat rejects;
        // resolve offsets for now until the date is complete
        const moment : Date = isNaN( at.getTime() ) ? new Date() : at;

        const list : Array<DropdownInput.Choice> = zones.map( ( tz : string ) : DropdownInput.Choice =>
        {
            const offset       : number = TimezoneUtils.getTimezoneOffset( tz, moment );
            const short_abbrev : string = TimezoneUtils.getTimezoneAbbrev( tz, "short", moment );

            let value : string;
            switch( valueType )
            {
                case TimezoneUtils.ValueType.CITY   : value = tz; break;
                case TimezoneUtils.ValueType.UTC    : value = StringUtils.format( "UTC{0}", TimezoneUtils.offsetToString( offset, false ) ); break;
                case TimezoneUtils.ValueType.ABBREV : value = short_abbrev; break;
            }

            const label : string = TimezoneUtils.getLabel( tz, labelType, dense, moment );

            return  {
                        value : value,
                        label : StringUtils.format( "{0} (UTC{1})", label, TimezoneUtils.offsetToString( offset, true ) ),
                        data  : offset
                    };
        } );

        // sort by timezone offset (in minutes), furthest ahead of UTC first
        list.sort( ( a : DropdownInput.Choice, b : DropdownInput.Choice ) => ( b.data as number ) - ( a.data as number ) );

        return list;
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Resolves the display label for a single IANA timezone, given a label type.
     *
     * @param tz - The IANA timezone identifier ( e.g. "America/Chicago" ).
     * @param labelType - `ABBREV` for "CDT: Central Daylight Time", `CITY` for "America/Chicago".
     * @param dense - When `labelType` is `CITY`, shows just the city portion ( "Chicago" )
     *                instead of the full "Region/City" name.
     * @param at - The moment to resolve the abbreviation for ( defaults to now, since
     *             abbreviations change across DST boundaries ).
     * @returns The resolved label string.
     */
    public static getLabel( tz : string, labelType : TimezoneUtils.LabelType, dense : boolean = false, at : Date = new Date() ) : string
    {
        switch( labelType )
        {
            case TimezoneUtils.LabelType.ABBREV : return StringUtils.format( "{0}: {1}", TimezoneUtils.getTimezoneAbbrev( tz, "short", at ), TimezoneUtils.getTimezoneAbbrev( tz, "long", at ) );
            case TimezoneUtils.LabelType.CITY   : return dense ? tz.split( '/' )[1] : StringUtils.replaceAll( tz, "_", " " );
        }
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Computes the UTC offset (in minutes) for a given IANA timezone at a given moment.
     *
     * @param tz - The IANA timezone identifier ( e.g. "America/New_York" ).
     * @param at - The moment to compute the offset for ( defaults to now, since the offset
     *             changes across DST boundaries ).
     * @returns The offset from UTC, in minutes ( positive = ahead of UTC ).
     */
    public static getTimezoneOffset( tz : string, at : Date = new Date() ) : number
    {
        const now : Date = at;

        // Get the time in UTC
        const utcYear   : number = now.getUTCFullYear();
        const utcMonth  : number = now.getUTCMonth();
        const utcDate   : number = now.getUTCDate();
        const utcHour   : number = now.getUTCHours();
        const utcMinute : number = now.getUTCMinutes();
        const utcSecond : number = now.getUTCSeconds();

        // Get the time in the target timezone
        const dtf : Intl.DateTimeFormat = new Intl.DateTimeFormat( 'en-US', {
            timeZone : tz,
            hour12   : false,
            year     : 'numeric', month : '2-digit', day : '2-digit',
            hour     : '2-digit', minute : '2-digit', second : '2-digit'
        } );
        const parts : Array<Intl.DateTimeFormatPart> = dtf.formatToParts( now );
        const year   : number = Number( parts.find( p => p.type === 'year' )?.value );
        const month  : number = Number( parts.find( p => p.type === 'month' )?.value ) - 1;
        const day    : number = Number( parts.find( p => p.type === 'day' )?.value );
        const hour   : number = Number( parts.find( p => p.type === 'hour' )?.value );
        const minute : number = Number( parts.find( p => p.type === 'minute' )?.value );
        const second : number = Number( parts.find( p => p.type === 'second' )?.value );

        // Create a date in the target timezone (interpreted as local time)
        const tzDate     : Date = new Date( Date.UTC( year, month, day, hour, minute, second ) );
        const utcDateObj : Date = new Date( Date.UTC( utcYear, utcMonth, utcDate, utcHour, utcMinute, utcSecond ) );

        // Offset in minutes from UTC
        const offset : number = ( tzDate.getTime() - utcDateObj.getTime() ) / 60000;
        return offset;
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Resolves the "EST"/"EDT"-style ( short ) or "Eastern Standard Time"-style ( long )
     * abbreviation for a given IANA timezone at a given moment, normalized to "UTC±HH:MM"
     * when no named abbreviation is available.
     *
     * @param tz - The IANA timezone identifier ( e.g. "America/New_York" ).
     * @param format - "short" for "EDT", "long" for "Eastern Daylight Time".
     * @param at - The moment to resolve the abbreviation for ( defaults to now, since
     *             abbreviations can change across DST boundaries ).
     * @returns The resolved abbreviation string, or "" if it could not be determined.
     */
    public static getTimezoneAbbrev( tz : string, format : "short" | "long", at : Date = new Date() ) : string
    {
        try
        {
            const dtf : Intl.DateTimeFormat = new Intl.DateTimeFormat( 'en-US', {
                timeZone     : tz,
                timeZoneName : format
            } );

        
            const parts : Array<Intl.DateTimeFormatPart> = dtf.formatToParts( at );
            const tzName : string | undefined = parts.find( p => p.type === 'timeZoneName' )?.value;
            if( tzName === undefined || tzName.trim().length === 0 )return "";

            //console.log('tz', tz, parts );

            // Examples you might see:
            //  - "EST", "EDT"
            //  - "GMT-4", "GMT-04:00"
            let name : string = tzName.trim();

            // Normalize "GMT" -> "UTC" to match your UI
            name = name.replace( /^GMT/, 'UTC' );

            // Normalize offsets like "UTC-4" or "UTC-0400" to "UTC-04:00"
            const m : RegExpMatchArray | null = name.match( /^UTC([+-])(\d{1,2})(?::?(\d{2}))?$/ );
            if( m !== null )
            {
                const sign : string = m[1];
                const hh   : string = String( m[2] ).padStart( 2, '0' );
                const mm   : string = ( m[3] ?? '00' ).padStart( 2, '0' );
                return `UTC${sign}${hh}:${mm}`;
            }

            return name;
        }
        catch
        {
            // Fallback: compute offset ourselves if Intl fails for some reason.
            const offset : number = TimezoneUtils.getTimezoneOffset( tz );
            return `UTC${TimezoneUtils.offsetToString( offset, true )}`;
        }
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Formats a UTC offset (in minutes) as a signed "±HH" or "±HH:MM" string.
     *
     * @param offset - The offset from UTC, in minutes.
     * @param include_colon - Whether to separate hours/minutes with a colon.
     * @returns The formatted offset string ( e.g. "-05:00" or "-0500" ).
     */
    public static offsetToString( offset : number, include_colon : boolean ) : string
    {
        const sign      : string = offset >= 0 ? "+" : "-";
        const absOffset : number = Math.abs( offset );
        const hours     : number = Math.floor( absOffset / 60 );
        const minutes   : number = Math.floor( absOffset % 60 );

        let reply : string = `${sign}${ String( hours ).padStart( 2, "0" ) }`;
        if( include_colon )reply += ":";
        reply += `${ String( minutes ).padStart( 2, "0" ) }`;
        return reply;
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Resolves the "CDT: Central Daylight Time"-style abbreviation label for a stored
     * timezone value, given the `ValueType` it was stored in and the candidate IANA zones
     * it could have come from ( a raw UTC offset is ambiguous on its own - e.g. "UTC-05:00"
     * matches both US Central ( standard time ) and US Eastern ( daylight time ), so the
     * candidate list narrows it to the zones actually offered in the picker ).
     *
     * @param value - The stored timezone value ( e.g. "UTC-05:00", "CDT", or an IANA name ).
     * @param valueType - The `ValueType` `value` was stored with.
     * @param zones - Candidate IANA timezone names to resolve `value` against.
     * @returns The "{short}: {long}" abbreviation label, or `value` unchanged if no candidate matches.
     */
    public static resolveAbbrev( value : string, valueType : TimezoneUtils.ValueType, zones : Array<string> ) : string
    {
        let tz : string | undefined;

        switch( valueType )
        {
            case TimezoneUtils.ValueType.CITY   : tz = zones.find( ( zone : string ) => zone === value ); break;
            case TimezoneUtils.ValueType.ABBREV : tz = zones.find( ( zone : string ) => TimezoneUtils.getTimezoneAbbrev( zone, "short" ) === value ); break;
            case TimezoneUtils.ValueType.UTC    :
                const minutes : number | null = TimezoneUtils.parseUtcOffsetToMinutes( value );
                tz = minutes === null ? undefined : zones.find( ( zone : string ) => TimezoneUtils.getTimezoneOffset( zone ) === minutes );
                break;
        }

        return tz === undefined ? value : TimezoneUtils.getLabel( tz, TimezoneUtils.LabelType.ABBREV );
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Parses a "UTC±HH:MM" ( or "UTC±HHMM" ) string into an offset in minutes.
     *
     * @param value - The offset string to parse ( e.g. "UTC-05:00" ).
     * @returns The offset in minutes, or `null` if `value` isn't in the expected format.
     */
    public static parseUtcOffsetToMinutes( value : string ) : number | null
    {
        if( value === null || value === undefined )return null;

        const m : RegExpMatchArray | null = value.match( /^UTC([+-])(\d{2}):?(\d{2})$/ );
        if( m === null )return null;

        const sign    : number = m[1] === '-' ? -1 : 1;
        const hours   : number = Number( m[2] );
        const minutes : number = Number( m[3] );
        return sign * ( ( hours * 60 ) + minutes );
    }

    /////////////////////////////////////////////////////////////////////////////////
    /**
     * Converts a legacy "GMT±HHMM" ( optionally with a trailing abbreviation, e.g.
     * "GMT-0500 EST" ) value into the "UTC±HH:MM" format used by `ValueType.UTC`.
     * Values that aren't in "GMT..." form are returned unchanged.
     *
     * @param value - The value to normalize.
     * @returns The normalized "UTC±HH:MM" string, or the original value if unchanged.
     */
    public static normalizeGmtToUtc( value : string ) : string
    {
        if( value === null || !value.startsWith( 'GMT' ) )return value;

        const gmtMatch : RegExpMatchArray | null = value.match( /GMT([+-]\d{4})/ );
        if( gmtMatch === null )return value;

        return `UTC${gmtMatch[1]}`;
    }
}

export namespace TimezoneUtils
{
    export enum ValueType
    {
        CITY   = "city",
        UTC    = "utc",
        ABBREV = "abbrev"
    }

    export enum LabelType
    {
        CITY   = "city",
        ABBREV = "abbrev"
    }
}

export default TimezoneUtils;
//
// eof
//
